import {
    Alert, Button, Col, Image, Input, Row, Space, Steps, Typography, theme,
} from 'antd';
import type { FC } from 'react';
import {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { QrcodeOutlined, SafetyOutlined, LockOutlined } from '@ant-design/icons';
import { startSetup2FA, startVerifySetup2FA, useAuthStore } from '../../store/auth.store';
import BackupCodesDisplay from './BackupCodesDisplay';

import { GOOGLE_AUTH_PLAY_STORE_URL, GOOGLE_AUTH_APP_STORE_URL } from '../../const/environments.constants';

const { Title, Text, Link } = Typography;
const CODE_LENGTH = 6;

interface ITwoFactorSetupWizardProps {
	onComplete: () => void;
	onSkip?: () => void;
	onBack?: () => void;
	embedded?: boolean;
}

const TwoFactorSetupWizard: FC<ITwoFactorSetupWizardProps> = ({
    onComplete, onSkip, onBack, embedded,
}) => {
    const { t } = useTranslation('login');
    const { token: themeToken } = theme.useToken();

    const { loading, error, setupData } = useAuthStore(
        useShallow((state) => ({ loading: state.loading, error: state.error, setupData: state.setupData })),
    );

    const [currentStep, setCurrentStep] = useState(0);
    const [code, setCode] = useState('');
    const [verified, setVerified] = useState(false);

    useEffect(() => { startSetup2FA(); }, []);

    const handleCodeChange = useCallback((value: string) => {
        const cleaned = value.replace(/\D/g, '').slice(0, CODE_LENGTH);
        setCode(cleaned);
        if (cleaned.length === CODE_LENGTH) {
            startVerifySetup2FA(cleaned).then(() => {
                const hasError = useAuthStore.getState().error;
                if (!hasError) setVerified(true);
            });
        }
    }, []);

    useEffect(() => {
        if (verified && currentStep === 2) onComplete();
    }, [verified, currentStep, onComplete]);

    const steps = useMemo(() => [
        { title: t('SETUP_2FA.STEP_QR'), icon: <QrcodeOutlined /> },
        { title: t('SETUP_2FA.STEP_BACKUP'), icon: <SafetyOutlined /> },
        { title: t('SETUP_2FA.STEP_VERIFY'), icon: <LockOutlined /> },
    ], [t]);

    const renderStep = useCallback(() => {
        switch (currentStep) {
        case 0:
            return (
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    {setupData?.qrCode ? (
                        <>
                            <Row gutter={24} align="top">
                                <Col xs={24} sm={12}>
                                    <Space direction="vertical" size={12}>
                                        <Text style={{ lineHeight: 1.6 }}>{t('SETUP_2FA.QR_DESCRIPTION')}</Text>
                                        <div>
                                            <Text type="secondary" style={{ fontSize: 12 }}>{t('SETUP_2FA.MANUAL_ENTRY')}</Text>
                                            <Text
                                                copyable
                                                code
                                                style={{
                                                    fontSize: 13, fontFamily: 'monospace', letterSpacing: 1, display: 'block', marginTop: 4,
                                                }}
                                            >
                                                {setupData.secret}
                                            </Text>
                                        </div>
                                    </Space>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                        <div style={{
                                            background: themeToken.colorBgContainer,
                                            padding: 16,
                                            borderRadius: 12,
                                            border: `1px solid ${themeToken.colorBorder}`,
                                            display: 'inline-block',
                                        }}
                                        >
                                            <Image src={setupData.qrCode} alt="QR Code" width={200} height={200} preview={false} />
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', background: themeToken.colorFillTertiary, borderRadius: 8,
                            }}
                            >
                                <Text type="secondary" style={{ fontSize: 12, flex: 1 }}>{t('SETUP_2FA.QR_APP_HINT')}</Text>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <Link href={GOOGLE_AUTH_PLAY_STORE_URL} target="_blank">
                                        <Image src="https://picker-dashboard.s3.amazonaws.com/icons/google-play.svg" alt="Google Play" height={28} preview={false} />
                                    </Link>
                                    <Link href={GOOGLE_AUTH_APP_STORE_URL} target="_blank">
                                        <Image src="https://picker-dashboard.s3.amazonaws.com/icons/app-store.png" alt="App Store" height={28} preview={false} />
                                    </Link>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                            <Text type="secondary">{t('SETUP_2FA.LOADING_QR')}</Text>
                        </div>
                    )}
                    <Button type="primary" block size="large" onClick={() => setCurrentStep(1)} disabled={!setupData?.qrCode}>
                        {t('CONTINUE')}
                    </Button>
                </Space>
            );

        case 1:
            return setupData?.backupCodes ? (
                <BackupCodesDisplay codes={setupData.backupCodes} onDone={() => setCurrentStep(2)} />
            ) : null;

        case 2:
            return (
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <Row gutter={24} align="middle">
                        <Col xs={24} sm={12}>
                            <Space direction="vertical" size={12}>
                                <Text style={{ lineHeight: 1.6 }}>{t('SETUP_2FA.VERIFY_DESCRIPTION')}</Text>
                                <Text type="secondary" style={{ fontSize: 12 }}>{t('TWO_FACTOR.CODE_HINT')}</Text>
                            </Space>
                        </Col>
                        <Col xs={24} sm={12}>
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <Input
                                    size="large"
                                    value={code}
                                    onChange={(e) => handleCodeChange(e.target.value)}
                                    placeholder="000000"
                                    maxLength={CODE_LENGTH}
                                    disabled={loading}
                                    autoFocus
                                    style={{
                                        textAlign: 'center', fontSize: 24, letterSpacing: 8, fontWeight: 600,
                                    }}
                                />
                            </div>
                        </Col>
                    </Row>
                    <Button type="link" block onClick={() => { setCode(''); useAuthStore.setState({ error: null }); setCurrentStep(0); }}>
                        {t('SETUP_2FA.BACK_TO_QR')}
                    </Button>
                </Space>
            );

        default:
            return null;
        }
    }, [currentStep, setupData, code, loading, handleCodeChange, t, themeToken]);

    const content = (
        <Space direction="vertical" size={24} style={{ width: '100%' }}>
            {!embedded && (
                <Title level={2} style={{ margin: 0, color: 'var(--color-primary)', textAlign: 'center' }}>
                    {t('SETUP_2FA.TITLE')}
                </Title>
            )}

            <Steps current={currentStep} items={steps} size="small" />

            {error && <Alert closable={false} message={error} type="error" showIcon />}

            {renderStep()}

            {onSkip && currentStep === 0 && (
                <Button type="link" block onClick={onSkip}>{t('SETUP_2FA.SKIP')}</Button>
            )}
            {onBack && !embedded && (
                <Button type="link" block onClick={onBack}>{t('TWO_FACTOR.BACK_TO_LOGIN')}</Button>
            )}
        </Space>
    );

    if (embedded) return content;

    return (
        <div style={{ maxWidth: 560, margin: '0 auto', padding: '40px 24px' }}>
            {content}
        </div>
    );
};

export default TwoFactorSetupWizard;
